import * as Actions from './Actions'
import * as OverpanelActions from './overpanels'
import * as UnifiedActions from './unified-actions'
import { fetch, post, postJson } from '../../calculator/lib/xhr'
import {
  INSTRUMENTATION_TYPES,
  ActionTypes as types,
  CLIENT_MISSING_REQUIRED_NOTE_FIELD,
  OPEN_BANKING_FLOWS,
  RTB_CONTINGENCY_RESOLVED,
} from '../constants'
import * as travelRuleUtils from '../utils/travelRuleUtils'
import { routeTo } from '../utils/routeHelper'
import { getRecentActions } from '../../transfer/middlewares/action-history'
import {
  getAutoSelectedFundingOption,
  removePreferredFromSourcesInOption,
  addPreferredToSourceInOption,
  shouldForceAutoSelectedOption,
  getSingleFundingOption,
  isFundingOptionAvailable,
  getOptionById,
  getRelevantFundingSource,
  buildFundingSourceAccountType,
} from '../utils/funding-option-utils'
import { getFlowType, getUrlFragment } from '../utils/url-utils'
import FeaturesConfig from '../../../../config/features.json'
import { FundingOptionSelector, SendSuccess } from 'transfer/lazy-components'
import { isIframe } from 'iframe-support'
import {
  threeDSDDCCallPromise,
  extract3DS2Contingency,
  track3ds2PostDDCAnalytics,
  collectBrowserContext,
} from '../utils/3ds-utils'
import { isEmpty, get } from 'lodash'
import { trackAction } from '../../lib/analytics/fpti'
import {
  MONEY_PAGE,
  INSTRUMENT_TYPES,
  CONTINGENCY,
  PAYMENT_TYPE,
  ENV_TYPE,
} from '../../../../lib/constants'
import { rankingPreferencesHelper } from './sendHelpers'
import { batch } from 'react-redux' // eslint-disable-line no-restricted-imports
import { calculateAttributesToCollect } from '../utils/travelRuleUtils'
import { isHideShippingAddress } from '../../../../lib/address'
import { isEmail, isPhone } from '../../../../shared/validators'
import logger from 'loglevel'
import { getSessionDataPayload } from 'transfer/utils/session'
import { isInstrumentDebitFailedError } from '../utils/fiFailureUtils'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'
import { getMainWindow } from '../../lib/iframeUtils'
import { isDataCollectionEnabled } from '../../experiments/ddcContingency'
import { resolvePaymentContingenciesSync } from './payment-contingency/paymentContingencyDispatchers'
import { blockPayment } from './payment-contingency/paymentContingencyActions'
import { isMoneyMovementTrmt } from '../../experiments/easeOfPaymentsFeatures'
import { removePreselectionLogic } from '../../experiments/removePreselectionLogic'

function makeFundingOptionsCall( // NOSONAR
  dispatch,
  state,
  {
    isExternal = false,
    conversionType = null,
    preselectedFundingOption = {},
    instrumentIds = [],
    handleFISelectorNext = null,
    fundingOptionsRefetched = false,
  } = {}
) {
  const {
    server: {
      env,
      groupFeatures: { shouldShowSelectFundingOptionsPage = false },
      flowIntent,
    },
    note: { note = '' },
    amount: { amount, currencyCode },
    recipient: {
      token,
      payerId = '',
      recipient,
      networkId = '',
      displayName = '',
      thumbnailUrl = '',
      moneyPageOverPanel,
      pispRecipientType = '',
      pispRecipientId = '',
    },
    address: {
      selectedShippingAddress: { id: selectedShippingAddressId },
    },
    transaction: {
      isPurchase,
      selectedFundingOption: { funding_sources },
      successfullyAddedFundingInstrumentIds,
      serviceReturnedType,
      shippingAddressCollectionPreference,
      isSenderReturningFromCIP,
      anonymousStatus,
      selectedPurposeOfPaymentOption,
    },
    unilateral: {
      countryCode: unilateralCountry,
      name: recipientFullName,
    } = {},
    crossBorder: { toCurrencyCode, fromCurrencyCode, fxQuoteId },
    external: {
      donationContext,
      recurring,
      donateToken,
      fromUL,
      sdkMeta,
      targetMeta,
      transaction_attribute,
    },
    donate: { donationAnonymousStatus },
    group: { id: groupId },
  } = state

  const ignorePreferred = Boolean(successfullyAddedFundingInstrumentIds)
  // our server uses both of these, and also is checking for the string "true"
  const action = isPurchase ? 'buy' : 'send'
  const goods = isPurchase ? 'true' : 'false'

  let paymentCardId

  // grab paymentCardId if user is changing currency conversion type
  if (conversionType) {
    const paymentCard =
      funding_sources &&
      funding_sources.find(
        source => source.instrument_type === INSTRUMENT_TYPES.PAYMENT_CARD
      )
    paymentCardId = paymentCard && paymentCard.id
  }

  // For mandatory FI, generate additional params for funding_option call
  const preferences = rankingPreferencesHelper({
    conversionType,
    instrumentIds,
    preselectedFundingOption,
  })

  const browserData = collectBrowserContext()
  const browserContext = {
    screenHeight: browserData.browser_screen_height,
    screenWidth: browserData.browser_screen_width,
    colorDepth: browserData.browser_color_depth,
    timezoneOffsetMinutes: browserData.browser_timezone_offset_minutes,
    language: browserData.browser_language,
    javaEnabled: browserData.browser_java_enabled,
  }

  const shouldSendShippingAddress = !isHideShippingAddress(
    shippingAddressCollectionPreference
  )

  const shippingAddressId = selectedShippingAddressId

  // query params being sent to the server
  const payload = {
    env,
    payerId,
    networkId,
    recipient,
    displayName,
    thumbnailUrl,
    amount,
    ...(shouldSendShippingAddress && { shippingAddressId }),
    currencyCode,
    goods,
    action,
    serviceReturnedType,
    note,
    flowType: isPurchase ? 'buy' : 'send',
    paymentCardId,
    conversionType,
    moneyPageExperience: moneyPageOverPanel,
    recipientCountry: unilateralCountry,
    successfullyAddedFundingInstrumentIds,
    // mixin additional fields based on env
    ...(recipientFullName && { recipientFullName }), // kyc
    ...(env === ENV_TYPE.DONATE && {
      donationContext,
      recurring,
      donateToken,
      fromUL,
      sdkMeta,
      targetMeta,
      transaction_attribute,
      donationAnonymousStatus,
    }),
    ...(env === ENV_TYPE.PISP &&
      pispRecipientType &&
      pispRecipientId && { pispRecipientType, pispRecipientId }), // this is required for funding options calls
    token,
    browserContext,
    flowIntent,
    fxQuoteId,
    ...getSessionDataPayload(),
    groupId,
    isAnonymousPayment: !!anonymousStatus,
    ...(selectedPurposeOfPaymentOption?.code && {
      purposeCode: selectedPurposeOfPaymentOption?.code,
    }),
  }

  if (preferences) {
    payload.preferences = preferences
  }

  const url = '/myaccount/transfer/fi'

  // prettier-ignore

  post(url, payload)
    .then(resolvePaymentContingenciesSync(dispatch)) // callback returns another function that receives and returns the response
    .then(response => {
      const { data = {} } = response
      let responseData = data
      // Add "conversionType" to the funding option call response data. In the case of funding a cross currency transaction
      // using card, we need to know the conversion type to show the conversion message "Converst currency with PAyPal | card"
      responseData = { ...responseData, conversionType }

      // As we have debounced pricing call in cross currency scenario, sometimes
      // amount in store mismatch with the amount sent to planning, hence here
      // we use amount sent to planning to update amount in store so that we have
      // consistent amount on Review and Success screen.
      if (
        toCurrencyCode !== fromCurrencyCode &&
        responseData.destinationAmount &&
        responseData.destinationAmount.value &&
        responseData.destinationAmount.currency
      ) {
        dispatch(Actions.changeAmount(responseData.destinationAmount.value))
        dispatch(
          Actions.changeCurrency(responseData.destinationAmount.currency)
        )
      }

      if (responseData.hasSenderCipVerificationContingency) {
        if (isSenderReturningFromCIP) {
          return dispatch(routeReturningUserFromCip())
        }
        return dispatch(
          redirectSenderCipVerification({
            senderCipVerification: responseData.senderCipVerification,
            hasSenderCipVerificationContingency:
              responseData.hasSenderCipVerificationContingency,
            env,
          })
        )
      }

      dispatch(
        receivedFundingOptions(
          responseData,
          shouldShowSelectFundingOptionsPage,
          ignorePreferred,
          fundingOptionsRefetched
        )
      )

      if (handleFISelectorNext) {
        handleFISelectorNext()
      }
    })
    .catch(// prettier-ignore
      async function (err) { // NOSONAR
      // Handle error that are specific for Groups
      if (env === ENV_TYPE.GROUPS) {
        const handleGroupsError = Actions.getGroupsErrorHandler(err)

        if (handleGroupsError) {
          return handleGroupsError(dispatch)
        }
      }

      switch (err.code) {
        case 'negativeBalance':
          const negativeBalanceUrl = '/myaccount/transfer/flow/fi/neg'
          const negativeBalancepayload = {
            flowType: getFlowType(),
            amount: amount,
            currencyCode: currencyCode,
            payerId,
            networkId,
            recipient: recipient,
            displayName,
            thumbnailUrl,
            flowIntent,
            env,
            token,
            ...getSessionDataPayload(),
          }
          const mainWindow = isIframe() ? window.top : window
        
          const response = await post(
            negativeBalanceUrl,
            negativeBalancepayload
          )
          const { redirectionUrl } = response
          mainWindow.location.href = redirectionUrl
        
          return
        case 'unilateral':
          return dispatch(showUnilateral(state.user.country))

        case 'noFundingSource':
        case 'errorExpiredCreditCard':
          const unavailableFI = Boolean(
            successfullyAddedFundingInstrumentIds.length
          )
          return dispatch(Actions.noFundingSource(unavailableFI))

        case 'authRequired':
          return // do nothing bc we are redirecting the user to auth flow in xhr.js

        case 'cipRequired':
          /**
           * For AU users who haven't completed CIP and want to Send Money F&F,
           * we show an overpanel that can redirect them to CIP flow.
           */
          return dispatch(
            OverpanelActions.toggleConfirmIdentityOverpanel({ show: true })
          )
        case CLIENT_MISSING_REQUIRED_NOTE_FIELD:
          return dispatch(Actions.mandatoryNoteMissing(true))

        case 'paymentBlocked':
          return dispatch(blockPayment())

        case 'contingencyRejection':
          return // don't throw an error, just don't continue with transaction.

        default:
          return dispatch(Actions.showError(err))
      }
    })
}

export function getAnalyticsMeta(type, payload) {
  return { analytics: { type, payload } }
}

export function fetchFundingOptions({
  conversionType = null,
  preselectedFundingOption = {},
  instrumentIds = [],
  handleFISelectorNext = null,
  fundingOptionsRefetched = false,
} = {}) {
  // preload the chunk file
  FundingOptionSelector.preload()
  // prettier-ignore
  return async (dispatch, getState) => {  // NOSONAR
    const {
      user: { requireSenderInstrumentAddition = false },
    } = getState()

    if (requireSenderInstrumentAddition) {
      // no available FIs to use for the current transaction, send user to wallet
      dispatch(Actions.setIsLoading(true))
      return dispatch(redirectToAddFI())
    }

    makeFundingOptionsCall(dispatch, getState(), {
      conversionType,
      instrumentIds,
      preselectedFundingOption,
      handleFISelectorNext,
      fundingOptionsRefetched,
    })

    dispatch({
      type: types.TRANSACTION.FETCH_FUNDING_OPTIONS,
    })
  }
}

export function getSelectedFundingOption({
  addedCards,
  preselectedFundingOption,
  fundingOptions,
  isPurchase,
  sourceIdsToMatch,
  ignorePreferred,
  showFISelector,
  fundingOptionIdFromSession,
  shouldShowSelectFundingOptionsPage,
}) {
  let selectedFundingOption
  if (removePreselectionLogic()) {
    if (!isEmpty(preselectedFundingOption)) {
      return preselectedFundingOption
    }
  } else {
    // try to automatically select a funding option from payload data
    const [newestAddedCard] = addedCards
    selectedFundingOption = getAutoSelectedFundingOption(
      fundingOptions,
      isPurchase,
      sourceIdsToMatch,
      newestAddedCard,
      ignorePreferred
    )

    // When showFISelector is False and there is a preselectedFundingOption,
    // we make the preselectedFundingOption and don't show the funding option selector
    if (!showFISelector && !isEmpty(preselectedFundingOption)) {
      selectedFundingOption = preselectedFundingOption
    }

    if (fundingOptionIdFromSession) {
      selectedFundingOption = getOptionById(
        fundingOptions,
        fundingOptionIdFromSession
      )
    }

    if (
      !selectedFundingOption &&
      shouldForceAutoSelectedOption(
        isPurchase,
        shouldShowSelectFundingOptionsPage
      )
    ) {
      // if not in the choice experiment, we need to make sure a funding option is selected since we won't be
      // forced to see the interstitial initially
      selectedFundingOption = getSingleFundingOption(fundingOptions)
    }
  }
  return selectedFundingOption
}

export function receivedFundingOptions(
  fundingData,
  shouldShowSelectFundingOptionsPage,
  ignorePreferred,
  fundingOptionsRefetched
) {
  const {
    fundingOptions = [],
    disallowedSources = [],
    complianceInfo = {},
    isPaypalAccount = false,
    additionalDisclosureActions = [],
    xbActualRecipientCountry = '',
    xbChosenCountry = '',
    xbCountryMismatch = false,
    riskHoldOverpanelVariant = '',
    isPreferredSourceDisallowed,
    conversionType,
    showFISelector = true,
    selectedFundingOptionId: fundingOptionIdFromSession = '',
    showStateMandatedWarning = false,
    paymentReviewDisclosureMessage = '',
    paymentTypeReviewMessage = '',
    items = [],
    shippingDetails = {},
    shippingType = '',
    purchaseConsentPreChecked = false,
    purchaseConsentChecked = false,
    purchaseConsentLabel = '',
    purchaseConsentRequired = false,
    userAgreementConsentLabel = '',
    userAgreementConsentRequired = false,
    userAgreementConsentMinVersion = '',
    userAgreementConsentMaxVersion = '',
    purposeCodeReviewDisclosureMessage = '',
  } = fundingData

  const xbData = {
    xbActualRecipientCountry,
    xbChosenCountry,
    xbCountryMismatch,
  }

  return (dispatch, getState) => {
    const {
      card: { addedCards },
      transaction: {
        isPurchase,
        fundingSourceIdsOfLastSelectedFundingOption: sourceIdsToMatch,
        successfullyAddedFundingInstrumentIds,
      },
    } = getState()

    // try to get the preselected Funding Option
    let preselectedFundingOption = {}
    fundingOptions.forEach(option => {
      if (option && option.preselected_funding_option) {
        preselectedFundingOption = option
      }
    })

    const selectedFundingOption = getSelectedFundingOption({
      addedCards,
      preselectedFundingOption,
      fundingOptions,
      isPurchase,
      sourceIdsToMatch,
      ignorePreferred,
      showFISelector,
      fundingOptionIdFromSession,
      shouldShowSelectFundingOptionsPage,
    })

    // Handle DDC calls by first collecting, storing all DDCs, and then firing
    // one by one sequentially in DeviceDataCollection.js
    const ddcCalls = collect3ds2DDCCalls(fundingOptions)
    if (ddcCalls.length > 0) {
      dispatch(storeAllDDC(ddcCalls))
    }

    // Toggle a warning when a new Funding Instrument is added in Wallet
    // And that FI is not available for the transaction
    const newFundingOptionIsAvailable = isFundingOptionAvailable(
      fundingOptions,
      successfullyAddedFundingInstrumentIds
    )
    if (successfullyAddedFundingInstrumentIds && !newFundingOptionIsAvailable) {
      dispatch(toggleFIUnavailableWarning(true))
    }

    /**
     * we also pass `iscvvshown` to analytics when the user sends money using a funding
     * option that has a 'CVV' contingency
     **/
    let analyticsMeta

    const { contingencies = [] } = selectedFundingOption || {} // we cannot assume that selectedFundingOption exists here
    const hasCVVContingency = contingencies.some(
      contingency => contingency.action === 'CVV_REQUIRED'
    )

    if (hasCVVContingency) {
      const data = {
        ...{ iscvvshown: true },
      }
      analyticsMeta = Actions.getAnalyticsMeta(
        INSTRUMENTATION_TYPES.ACTION_WITH_DATA,
        { linkName: 'continue', data }
      )
    }

    if (fundingOptionsRefetched) {
      dispatch({
        type: types.TRANSACTION.REFETCHED_FUNDING_OPTIONS,
        data: { shippingType },
      })
    }

    dispatch({
      type: types.TRANSACTION.RECEIVED_FUNDING_OPTIONS,
      meta: analyticsMeta,
      data: {
        ...xbData,
        items,
        shippingDetails,
        selectedFundingOption,
        preselectedFundingOption,
        additionalDisclosureActions,
        fundingOptions,
        riskHoldOverpanelVariant,
        disallowedSources,
        complianceInfo,
        isPaypalAccount,
        isPreferredSourceDisallowed,
        conversionType,
        showFISelector,
        showStateMandatedWarning,
        purposeCodeReviewDisclosureMessage,
        paymentReviewDisclosureMessage,
        paymentTypeReviewMessage,
        shippingType,
        purchaseConsentPreChecked,
        purchaseConsentChecked,
        purchaseConsentLabel,
        purchaseConsentRequired,
        userAgreementConsentRequired,
        userAgreementConsentLabel,
        userAgreementConsentMinVersion,
        userAgreementConsentMaxVersion,
      },
      receivedAt: Date.now(),
    })
  }
}

/**
 * This function collects all the DDC contingencies for 3DS2 from wallet
 * @param fundingOptions
 * @param dispatch
 */
export function collect3ds2DDCCalls(fundingOptions) {
  if (!isDataCollectionEnabled()) {
    return []
  }

  const ddcCalls = []
  fundingOptions.forEach(({ id = '', contingencies = [] }) => {
    contingencies.forEach(contingency => {
      const {
        action = '',
        three_d_secure_protocol: threeDSecureProtocol = '',
        jwt = '',
        three_ds_reference_id: threeDS2ReferenceId = '',
        device_data_collection_url: deviceDataCollectionUrl = '',
        device_data_collection_mode: deviceDataCollectionMode = '',
      } = contingency
      if (
        action === '3D_SECURE_DATA_COLLECTION_REQUIRED' &&
        threeDSecureProtocol === 'V2'
      ) {
        ddcCalls.push({
          jwt,
          deviceDataCollectionMode,
          deviceDataCollectionUrl,
          threeDSecureProtocol,
          threeDS2ReferenceId,
          fundingOptionId: id,
        })
      }
    })
  })
  return ddcCalls
}

export function endDDCCall() {
  return {
    type: types.TRANSACTION.END_DDC_CALL,
  }
}

export function startDDCCall(ddcInfo) {
  return {
    type: types.TRANSACTION.START_DDC_CALL,
    data: ddcInfo,
  }
}

export function storeAllDDC(ddcCalls) {
  return {
    type: types.TRANSACTION.STORE_ALL_DDC,
    data: { ddcCalls: ddcCalls },
  }
}

export function clearFundingOptions() {
  return {
    type: types.TRANSACTION.CLEAR_FUNDING_OPTIONS,
  }
}

export function setPurchaseConsentValue(purchaseConsentChecked) {
  return {
    type: types.TRANSACTION.PURCHASE_CONSENT_IS_CHECKED,
    data: { purchaseConsentChecked: purchaseConsentChecked },
  }
}

/**
 * Called when the user presses the "back" button on the browser for 3DS flow.
 */
export function refetchFundingOption3DS() {
  return {
    type: types.TRANSACTION.REFETCH_FUNDING_OPTIONS_3DS,
  }
}

/**
 * This function tries to resolve the DDC Contingency for a funding option
 * While the processing is happening the spinner screen is shown for atleast two seconds
 * and till we get a response from resolve-contingency API
 *
 * @param sendSubmit
 * @returns {Function}
 */

export function resolveDDC(
  sendSubmit,
  history = {},
  isGuest = false,
  browserContext = {}
) {
  return async (dispatch, getState) => {
    const {
      transaction: { selectedFundingOption = {}, refetchedFundingOptionFor3DS },
      server: { env = '' },
    } = getState()

    const { id: fundingOptionId } = selectedFundingOption

    const data = {
      fundingOptionId,
      browserContext,
      isGuest,
      env,
    }
    const url = `/myaccount/transfer/${
      isGuest ? 'guestLogin' : 'send'
    }/ddc-contingency`

    // The change here is trying to stop resolve DDC call that is running in the background when browser back is pressed.
    // When browser back is initiated refetchedFundingOptionFor3DS is set to true. So that the resolveDDC already initiated
    // can be cancelled. refetchedFundingOptionFor3DS is reset when TRANSACTION.RECEIVED_FUNDING_OPTIONS is triggered.
    if (refetchedFundingOptionFor3DS) {
      return
    }
    // start the spinner
    dispatch(OverpanelActions.toggle3DS2SpinnerOverpanel({ show: true }))

    // change the pglk as this action is trigerred by click from Send Payment Now
    trackAction('Send Payment Now')

    // Only call DDC if deviceDataCollectionMode is SYNCHRONOUS
    const ddcInfo = collect3ds2DDCCalls([selectedFundingOption])[0]
    const isSynchronous =
      get(ddcInfo, 'deviceDataCollectionMode', '') === 'SYNCHRONOUS'
    if (isSynchronous) {
      // Wait for DDC call promise to resolve before continuing
      await threeDSDDCCallPromise(dispatch, ddcInfo)
    }

    const startTime = performance.now()
    fetch(url, data)
      .then(response => {
        let endTime = performance.now()
        let timeDiff = (endTime - startTime) / 1000
        // check if the time taken for resolve contingency is greater than 2 seconds, stop the spinner,
        // if less than 2 seconds, show the spinner
        // The purpose of this code is to add a delay of minimum of 2 seconds
        while (timeDiff <= 2) {
          endTime = performance.now()
          timeDiff = (endTime - startTime) / 1000
        }
        // stop the spinner
        dispatch(OverpanelActions.toggle3DS2SpinnerOverpanel({ show: false }))

        track3ds2PostDDCAnalytics(isGuest, startTime, endTime)

        if (response && response.isDDCSuccess) {
          // show the challenge overpanel as there is another contingency in response of resolving the DDC Contingency
          if (response.threeDS2contingency) {
            dispatch({
              type: types.TRANSACTION.THREE_DS2_SECURE_AUTH_REQUIRED,
              data: response.threeDS2contingency,
            })
            // Adding setTimeout as multiple action dispatch causes the screen to freeze
            setTimeout(() => {
              sendSubmit()
              Actions.resetScrollPosition()
            }, 500)
          } else {
            // show the success screen as there is no challenge
            dispatch({ type: types.TRANSACTION.DDC_RESOLVED })
            sendSubmit()
            Actions.resetScrollPosition()
          }
        }
      })
      .catch(err => {
        dispatch(OverpanelActions.toggle3DS2SpinnerOverpanel({ show: false }))
        const endTime = performance.now()
        track3ds2PostDDCAnalytics(isGuest, startTime, endTime)
        setTimeout(() => {
          dispatch(Actions.showError(err))
        }, 500)
      })
  }
}

export function handleCVV(sendSubmit) {
  return (dispatch, getState) => {
    const {
      transaction: {
        selectedFundingOption: { id: fundingOptionId },
        cvv,
      },
      server: { env },
    } = getState()

    const sendData = {
      fundingOptionId,
      cvv,
      env,
    }

    dispatch({ type: types.TRANSACTION.REQUEST_CVV })

    const url = '/myaccount/transfer/send/cvv-contingency'
    post(url, sendData)
      .then(response => {
        dispatch({
          type: types.TRANSACTION.RESOLVE_CVV,
          data: response,
        })
        // Re-submit to proceed with transaction
        sendSubmit()
        Actions.resetScrollPosition()
      })
      .catch(err => {
        dispatch(Actions.showError(err))
      })
  }
}

export function handlePurchaseConsent(sendSubmit, isGuest = false) {
  return (dispatch, getState) => {
    const {
      transaction: {
        selectedFundingOption: { id: fundingOptionId },
        purchaseConsentChecked,
        purchaseConsentRequired,
      },
      server: { env },
    } = getState()

    const sendData = {
      fundingOptionId,
      purchaseConsentChecked,
      purchaseConsentRequired,
      env,
    }

    dispatch({ type: types.TRANSACTION.REQUEST_PURCHASE_CONSENT })

    const userFlowType = isGuest ? 'guestLogin' : 'send'
    const url = `/myaccount/transfer/${userFlowType}/purchase-consent-contingency`
    post(url, sendData)
      .then(response => {
        dispatch({ type: types.TRANSACTION.RESOLVE_PURCHASE_CONSENT })
        // Re-submit to proceed with transaction
        sendSubmit()
        Actions.resetScrollPosition()
      })
      .catch(err => {
        dispatch(Actions.showError(err))
      })
  }
}

// Resolve Contingency
export function handlePurposeCodeCollection(sendSubmit, isGuest = false) {
  return (dispatch, getState) => {
    const {
      transaction: {
        selectedFundingOption: { id: fundingOptionId },
        selectedPurposeOfPaymentOption,
      },
    } = getState()

    const sendData = {
      fundingOptionId,
      purposeCode: selectedPurposeOfPaymentOption?.code,
    }

    dispatch({ type: types.TRANSACTION.REQUEST_PURPOSE_CODE_INFORMATION })

    const userFlowType = isGuest ? 'guestLogin' : 'send'
    const url = `/myaccount/transfer/${userFlowType}/purpose-code-collection-contingency`
    post(url, sendData)
      .then(response => {
        dispatch({ type: types.TRANSACTION.RESOLVE_PURPOSE_CODE_CONTINGENCY })
        // Re-submit to proceed with transaction
        sendSubmit()
        Actions.resetScrollPosition()
      })
      .catch(err => {
        dispatch(Actions.showError(err))
      })
  }
}

export function handleThreeDS2Success(threeDS2Contingency) {
  return {
    type: types.TRANSACTION.RESOLVED_3DS2_AUTH_CONTINGENCY,
    data: threeDS2Contingency,
  }
}

export function handleThreeDS2Failure() {
  return { type: types.TRANSACTION.FAILED_3DS2_AUTH_CONTINGENCY }
}

export function handleThreeDS() {
  return { type: types.TRANSACTION.RESOLVE_3DS }
}

export function errorThreeDS(err) {
  return dispatch => {
    dispatch(OverpanelActions.toggle3DSecureOverpanel({ show: false }))
    dispatch(Actions.showError(err))
    dispatch(clearFundingOptions())
  }
}

export const setDualControlContext = ({ handleNext = null }) => {
  return dispatch => {
    dispatch({ type: types.TRANSACTION.SET_DUALCONTROL_CONTEXT })

    const url = '/myaccount/transfer/send/set/dc-context'
    post(url)
      .then(() => {
        logger.info('set Dualcontrol context')
        if (handleNext) {
          handleNext()
        }
      })
      .catch(err => {
        Actions.handleError(dispatch)(err)
      })
  }
}

export const getDCSendMoneyReqPayload = (
  state,
  { flowIsPayRequest, flowType } = {}
) => {
  const {
    recipient: { payerId = '', recipient, displayName = '', fullName = '' },
    transaction: { isPurchase },
    unilateral: { countryCode, name: recipientFullName } = {},
    amount: { amount, currencyCode },
    note: { note: description },
    address: {
      selectedShippingAddress: { id: selectedShippingAddressId } = {},
    },
    server: { requestId, requestLegId },
  } = state

  const recipientId = payerId || recipient
  let recipientType

  if (isEmail(recipientId)) {
    recipientType = 'EMAIL'
  } else if (isPhone(recipientId)) {
    recipientType = 'PHONE'
  } else {
    recipientType = 'ACCOUNT_NUMBER'
  }

  const payload = {
    payee: {
      id: recipientId,
      type: recipientType,
      name: displayName || fullName || recipientFullName,
      country_code: countryCode,
      is_paypal_account: !!payerId,
    },
    options: [
      {
        payment_amount: {
          currency: currencyCode,
          value: amount,
        },
      },
    ],
    payment_type: isPurchase ? PAYMENT_TYPE.PURCHASE : PAYMENT_TYPE.PERSONAL,
    ...(flowIsPayRequest
      ? {
          flow_type: flowType,
          pay_request_details: { requestId, requestLegId },
        }
      : {}),
  }
  return { payload, description, selectedShippingAddressId }
}

export function handleSendMoneyRequest() {
  return (dispatch, getState) => {
    dispatch({ type: types.TRANSACTION.SEND_MONEY })
    const flowType = getFlowType()
    const flowIsPayRequest = flowType === 'payRequest'
    const data = getDCSendMoneyReqPayload(getState(), {
      flowType,
      flowIsPayRequest,
    })

    const url = '/myaccount/transfer/send/approval-requests'
    post(url, data)
      .then(response => {
        if (flowIsPayRequest) {
          dispatch({ type: types.TRANSACTION.ADDED_FOR_APPROVAL })
          routeTo(`${getUrlFragment()}/success`)
        } else {
          dispatch(sentMoney(response))
        }
        Actions.resetScrollPosition()
      })
      .catch(err => {
        Actions.handleError(dispatch)(err)
      })
  }
}

export function handleSendSubmit() {
  return (dispatch, getState) => {
    const {
      server: { env, country },
      external: {
        donationContext,
        isRecurring,
        donationGiftAidCheckbox,
        recipientIdType,
        recipientId, // paypalme slug
      },
      amount: { amount, currencyCode },
      recipient: { recipient },
      transaction: {
        isPurchase,
        selectedFundingOption,
        selectedFundingOption: { id: fundingOptionId },
        selectedInstallmentPlan: { installmentTerm },
        pendingResolutions = [],
      },
      note: { note: description },
      user,
    } = getState()
    const dflFmxId = () => {
      const selectedFOExists = !!Object.keys(selectedFundingOption).length
      return (
        selectedFOExists &&
        selectedFundingOption.funding_sources.map(source => source.id)
      )
    }

    const usingPreferred = () => {
      const selectedFOExists = !!Object.keys(selectedFundingOption).length
      const answer =
        selectedFOExists &&
        selectedFundingOption.funding_sources.some(source => source.isPreferred)
      return answer ? 'Y' : 'N'
    }

    let fltp = ''
    switch (getFlowType()) {
      case 'buy':
        fltp = 'g&s'
        break
      case 'send':
        fltp = 'f&f'
        break
      default:
        break
    }

    const data = {
      fltp: fltp,
      dfl_fmxid: dflFmxId() || 'nodefault', // selected instruments
      is_pref: usingPreferred(),
      slug_name: recipientIdType === 'ppme' && recipientId,
      pp_flow: env,
    }

    const linkName = 'Send'

    dispatch({
      type: '', // empty call to hit the analytics middleware
      meta: getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
        linkName,
        data: data,
        trackType: 'link',
      }),
    })

    // For v1, installmentTerm is a Number, but should be string for ajax call to server
    // For v2, installmentTerm is in Iso8601 format as Service only accepts duration in that format
    const installment_term = installmentTerm && installmentTerm.toString()
    const action = isPurchase ? 'buy' : 'send'

    const sendData = {
      env,
      action,
      recipient,
      amount,
      currencyCode,
      fundingOptionId,
      description,
      installment_term,
      donationGiftAidCheckbox,
      flowType: getFlowType(),
      travelRuleRequirements: calculateAttributesToCollect(
        selectedFundingOption
      ),
      pendingResolutions,

      // external variables
      ...(env === ENV_TYPE.DONATE && { donationContext }),
    }

    if (env === ENV_TYPE.DONATE && isRecurring) {
      return
    }

    if (!travelRuleUtils.isTravelRule(selectedFundingOption)) {
      return dispatch(send(sendData))
    }

    if (
      travelRuleUtils.hasAllNeededTravelRuleInfo({
        ...user,
        selectedFundingOption,
      })
    ) {
      const travelRuleData = travelRuleUtils.massageTravelRuleDataForPost({
        ...user,
        selectedFundingOption,
        country,
      })

      return dispatch(postTravelRuleAndSend({ ...sendData, ...travelRuleData }))
    }

    // if travel rule but not all info is present, just return out
    return
  }
}

export function send(data) {
  // prefetch chunk file
  SendSuccess.preload()

  return (dispatch, getState) => {
    const {
      transaction: { fundingOptions, selectedFundingOption },
      recipient: { token },
    } = getState()

    // we see a few transactions per day where we POST to /send with an empty string
    // for fundingOptionId, so in this case, we are passing up extra client-side
    // data, and we're checking for empty fundingOptionId on server and logging
    // this stuff to CAL
    if (!data.fundingOptionId) {
      Object.assign(data, {
        actions: getRecentActions(), // already stringified
        fundingOptions: JSON.stringify(fundingOptions),
        selectedFundingOption: JSON.stringify(selectedFundingOption),
      })
    }

    dispatch({ type: types.TRANSACTION.SEND_MONEY })
    const url = '/myaccount/transfer/send'
    if (isMoneyMovementTrmt()) {
      data.token = token
    }
    post(url, data)
      .then(response => {
        dispatch(sentMoney(response))
        Actions.resetScrollPosition()
      })
      .catch(err => {
        if (err.code === CLIENT_MISSING_REQUIRED_NOTE_FIELD) {
          return dispatch(Actions.mandatoryNoteMissing(true))
        }

        if (isInstrumentDebitFailedError(err)) {
          return dispatch(
            UnifiedActions.fetchFundingOptionsAndShowFundingOptionsOverpanel()
          )
        }

        Actions.handleError(dispatch)(err)
      })
  }
}

/**
 * Post new travel rule data and send money
 * @param {}
 * @return {}
 */
export function postTravelRuleAndSend(data) {
  return dispatch => {
    dispatch({ type: types.TRANSACTION.POST_TRAVEL_RULE_DATA })
    dispatch({ type: types.TRANSACTION.SEND_MONEY })
    const url = '/myaccount/transfer/fi/travelRule'
    return post(url, data)
      .then(response => {
        dispatch({ type: types.TRANSACTION.POSTED_TRAVEL_RULE_DATA })
        dispatch(sentMoney(response))
      })
      .catch(err => {
        if (isInstrumentDebitFailedError(err)) {
          return dispatch(
            UnifiedActions.fetchFundingOptionsAndShowFundingOptionsOverpanel()
          )
        }

        Actions.handleError(dispatch)(err)
        Actions.resetScrollPosition()
      })
  }
}

// mostly used for analytics right? No, also used to populate info for success screen.
// TODO: any reason for using the thunk here?
export function sentMoney(response) {
  return (dispatch, getState) => {
    const {
      data: {
        transactions = {},
        compliance_info = {},
        amountFormatted,
        xsell = {},
        receiver = {},
      },
    } = response
    const {
      server: { env, paymentTypes },
      external: {
        showSuccessPage,
        recipientIdType,
        recipientId, // paypalme slug
      },
      note: { note },
      recipient: { moneyPageOverPanel },
      transaction: {
        selectedFundingOption = {},
        is3DS2ChallengeRequired = false,
      },
    } = getState()

    let overpanelShownToUser = MONEY_PAGE.CONTROL_WEB

    // new variants only support gs/ff
    if (paymentTypes.includes('PERSONAL')) {
      overpanelShownToUser = moneyPageOverPanel
    }

    const { payment_id, type = '' } = transactions
    const flowType = getFlowType()
    const pp_flow = env
    const has_note = note.length ? 1 : 0
    const threeDS2Contingency = extract3DS2Contingency(selectedFundingOption)
    const has3DS2Contingency = !isEmpty(threeDS2Contingency)
    const threeDS2ReferenceId = has3DS2Contingency
      ? threeDS2Contingency.three_ds_reference_id
      : ''

    const thumbnailUrl = receiver?.profilePhoto

    if (thumbnailUrl) {
      dispatch({
        type: types.RECIPIENT.UPDATE_RECEIVER_PHOTO,
        data: {
          receiver,
        },
      })
    }

    const data = {
      trid: payment_id,
      txn_amt: amountFormatted,
      type,
      pp_flow,
      slug_name: recipientIdType === 'ppme' && recipientId,
      fltp: undefined,
      has_note,
      is_3ds_eligible: has3DS2Contingency,
      '3ds_ref_id': threeDS2ReferenceId,
      challenge_shown: is3DS2ChallengeRequired,
      page_variant: overpanelShownToUser,
    }

    dispatch({
      type: types.TRANSACTION.SENT_MONEY,
      meta: Actions.getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
        linkName: flowType || 'sentMoneySuccessful',
        data,
      }),
      data: {
        paymentDetails: transactions,
        complianceInfo: compliance_info,
        amountFormatted,
      },
      receivedAt: Date.now(),
    })

    if (!isEmpty(xsell)) {
      dispatch({
        type: types.XSELL.DONE_XSELL_CONTENT,
        data: {
          xsell,
        },
      })
    }

    // we're also going to re-direct here after dispatching that message
    const targetUrl = `/homepage/${getFlowType()}/success`

    if (showSuccessPage) {
      routeTo(targetUrl, { replace: true })
    }

    dispatch({ type: 'externalHook:sendSuccess' })
  }
}

function showUnilateral(userCountryCode) {
  // default recipient's country to sender's country.
  // Limit this change to US for now.
  const countryFeatures = FeaturesConfig[userCountryCode] || {}
  if (countryFeatures.defaultUnilateralRecipientCountry) {
    return {
      type: types.TRANSACTION.SHOW_UNILATERAL,
      data: { userCountryCode },
    }
  }

  return {
    type: types.TRANSACTION.SHOW_UNILATERAL,
    data: { userCountryCode: '' },
  }
}

export function setPreferredFundingSource(fundingSourceId) {
  return dispatch => {
    const matchBalanceSourceIds = /^HO/ // each balance has it's own id, so we've been given api to pass just BALANCE to cover everything
    fundingSourceId = matchBalanceSourceIds.test(fundingSourceId)
      ? 'BALANCE'
      : fundingSourceId
    dispatch({ type: types.TRANSACTION.POST_PREFERRED_FUNDING_SOURCE })
    return post('/myaccount/transfer/setPreferredFundingSource', {
      fundingSourceId,
    })
      .then(response => {
        dispatch({ type: types.TRANSACTION.POSTED_PREFERRED_FUNDING_SOURCE })
        dispatch(updatePreferredInClientData(fundingSourceId))
      })
      .catch(Actions.handleError(dispatch))
  }
}

/**
 * An action which will take a target source id which has just been set preferred at service layer, and will manually
 * add the preferred tag to the funding options in client side data. We must also remove preferred tag from anything else
 * that was preferred. This is so that we don't have to fetch again when the user saves a source as preferred.
 */
function updatePreferredInClientData(targetSourceId) {
  return (dispatch, getState) => {
    const {
      transaction: { fundingOptions, selectedFundingOption },
    } = getState()
    const optionsWithPreferredRemoved = fundingOptions.map(
      removePreferredFromSourcesInOption
    )
    const optionsWithPreferredAdded = optionsWithPreferredRemoved.map(option =>
      addPreferredToSourceInOption(option, targetSourceId)
    )
    const selectedOptionWithPreferredRemoved = removePreferredFromSourcesInOption(
      selectedFundingOption
    )
    const selectedOptionWithPreferredAdded = addPreferredToSourceInOption(
      selectedOptionWithPreferredRemoved,
      targetSourceId
    )
    dispatch({
      type: types.TRANSACTION.UPDATE_PREFERRED_IN_CLIENT_DATA,
      data: {
        fundingOptions: optionsWithPreferredAdded,
        selectedFundingOption: selectedOptionWithPreferredAdded,
      },
    })
  }
}

function handleRTBResponse(
  response,
  resOptions,
  trackFIContingency,
  dispatch,
  handleNext
) {
  const { fundingOptions, fundingOptionId, action } = resOptions
  if (response.isRTBSuccess) {
    if (response.reviewRequired) {
      const bankWithEcheck =
        fundingOptions.find(({ id }) => id === fundingOptionId) || {}
      trackFIContingency(response.reviewRequired ? 'echeck' : 'success')
      batch(() => {
        dispatch(
          fetchFundingOptions({
            preselectedFundingOption: bankWithEcheck,
            handleFISelectorNext: handleNext,
          })
        )
        dispatch(Actions.changeSelectedFundingOption(fundingOptionId))
      })
    } else {
      trackFIContingency(response.eCheck ? 'echeck' : 'success')
      batch(() => {
        dispatch({
          type: types.TRANSACTION.RESOLVED_RTB_CONTINGENCY,
          data: {
            fundingOptionId,
            action,
            eCheck: response.eCheck,
          },
        })
        dispatch(Actions.changeSelectedFundingOption(fundingOptionId))
      })
      if (handleNext) {
        handleNext(RTB_CONTINGENCY_RESOLVED)
      }
    }
  }
}

function handleRTBError(
  err,
  errOptions,
  trackFIContingency,
  dispatch,
  handleNext
) {
  const { fundingOptionId, fundingOptions, payload } = errOptions
  if (err.code === 'bankLoginRequired') {
    // Open Banking REFRESH_REAL_TIME_BALANCE contingency failed with invalid credentials
    // We change the contingency from REFRESH to CONSENT

    trackFIContingency('invalid_credentials')
    dispatch(Actions.changeSelectedFundingOption(fundingOptionId))
    dispatch({
      type: types.TRANSACTION.RTB_INVALID_CREDENTIALS,
      data: {
        ...payload,
        newContingency: CONTINGENCY.INVALID_CREDENTIALS,
      },
    })
    if (handleNext) {
      handleNext()
    }
    return
  }
  trackFIContingency('failure')
  if (err.code === 'errorPaymentDeniedForBank') {
    const fundingOption =
      fundingOptions.find(({ id }) => id === fundingOptionId) || {}
    const fundingSource = getRelevantFundingSource(fundingOption) || {}
    const accountType = buildFundingSourceAccountType(fundingSource)

    batch(() => {
      dispatch({
        type: types.TRANSACTION.RTB_ERROR,
        data: {
          fundingOptionId,
        },
      })

      dispatch(toggleFIUnavailableWarning(true, accountType))
    })
  } else {
    // 500s and unhandled exceptions
    dispatch(Actions.showError(err))
  }
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
/**
 * Resolves the Open Banking RTB (Real Time Balance) REFRESH and CONSENT contingencies
 * @param {string} action - Contingency to resolve
 * @param {Function} handleNext - Callback to execute after REFRESH contingency is resolved
 * @param {string} fundingId - This is only used when action is CONTINGENCY.CONSENT as it is not available in the store
 * @returns {Function}
 */
export function resolveRTBContingency(
  action,
  handleNext,
  fundingOptionId,
  base64EncodedJWTErrorFromWallet,
  trackFIContingency = () => {}
) {
  const url = `/myaccount/transfer/send/rtb-contingency`
  const payload = {
    fundingOptionId,
    action,
    // CONTINGENCY.INVALID_CREDENTIALS is a wrapper around CONTINGENCY.REFRESH
    // used internally in p2pnodeweb to activate the CONSENT flow.
    ...(action === CONTINGENCY.INVALID_CREDENTIALS
      ? { action: CONTINGENCY.REFRESH }
      : {}),
    base64EncodedJWTErrorFromWallet,
  }

  return async (dispatch, getState) => {
    const {
      transaction: { fundingOptions },
      server: { env },
    } = getState()
    dispatch({ type: types.TRANSACTION.RESOLVE_RTB_CONTINGENCY })
    trackFIContingency()
    await fetch(url, payload)
      .then(response => {
        const resOptions = {
          env,
          fundingOptions,
          fundingOptionId,
          action,
        }
        if (response) {
          handleRTBResponse(
            response,
            resOptions,
            trackFIContingency,
            dispatch,
            handleNext
          )
        }
      })
      .catch(err => {
        const errOptions = {
          fundingOptionId,
          fundingOptions,
          payload,
        }
        handleRTBError(
          err,
          errOptions,
          trackFIContingency,
          dispatch,
          handleNext
        )
      })
  }
}

export function saveSuccessfullyAddedFundingInstrumentIds(
  successfullyAddedFundingInstrumentIds
) {
  return {
    type: types.TRANSACTION.SAVE_SUCCESS_ID,
    data: { successfullyAddedFundingInstrumentIds },
  }
}

export function setOpenBankingError(openBankingError) {
  return {
    type: types.TRANSACTION.SET_OPEN_BANKING_ERROR,
    data: { openBankingError },
  }
}

export function toggleFIUnavailableWarning(unavailableFI, accountType) {
  return {
    type: types.TRANSACTION.UNAVAILABLE_FI,
    data: { unavailableFI, accountType },
  }
}

export function redirectToBankLogin(data) {
  const mainWindow = isIframe() ? window.top : window
  const url = '/myaccount/transfer/flow/fi/banklogin'
  return dispatch => {
    return postJson(url, data)
      .then(({ redirectUrl }) => {
        mainWindow.location = redirectUrl
      })
      .catch(err => {
        Actions.handleError(dispatch)(err)
        Actions.resetScrollPosition()
      })
  }
}

function getAddFIPayload(state, override = {}) {
  return getSessionPayload(state, {
    ...override,
    obFlow: OPEN_BANKING_FLOWS.ADD_BANK,
  })
}

export function getSessionPayload(state, override = {}) {
  let flowType = getFlowType()
  if (!flowType && state.transaction.isPurchase !== undefined) {
    flowType = state.transaction.isPurchase ? 'buy' : 'send'
  }
  return {
    flowType,
    amount: state.amount.amount,
    currencyCode: state.amount.currencyCode,
    payerId: state.recipient.payerId,
    recipient: state.recipient.recipient,
    unilateralName: state.unilateral.name,
    unilateralCountryCode: state.unilateral.countryCode,
    mayCashAdvanceFeesApply: state.recipient.mayCashAdvanceFeesApply,
    isPurchase: state.transaction.isPurchase,
    personalContextID: state.transaction.personalContextID,
    purchaseContextID: state.transaction.purchaseContextID,
    displayName: state.recipient.displayName,
    thumbnailUrl: state.recipient.thumbnailUrl,
    networkId: state.recipient.networkId,
    flowIntent: state.server.flowIntent,
    note: state.note.note,
    token: state.recipient.token,
    isAShop: state.recipient.isAShop,
    ...override,
    ...getSessionDataPayload(),
  }
}

export function redirectToAddFI(override = {}) {
  const mainWindow = isIframe() ? window.top : window
  const url = '/myaccount/transfer/flow/fi/new'
  return (dispatch, getState) => {
    const state = getState()
    const payload = getAddFIPayload(state, override)
    post(url, payload)
      .then(response => {
        const { redirectURL = {} } = response
        tracking(Events.p2p_add_fi_screen_shown())
        mainWindow.location.assign(redirectURL)
      })
      .catch(err => {
        return dispatch(Actions.showError(err))
      })
  }
}

export function handleUserAgreementConsent(handleSubmit) {
  return (dispatch, getState) => {
    const {
      transaction: {
        selectedFundingOption: { id: fundingOptionId },
        userAgreementConsentRequired,
        userAgreementConsentMinVersion,
        userAgreementConsentMaxVersion,
      },
      server: { env },
    } = getState()

    const payload = {
      fundingOptionId,
      userAgreementConsentRequired,
      env,
      userAgreementConsentMinVersion,
      userAgreementConsentMaxVersion,
    }

    tracking(
      Events.active_consent_disclaimer_agree_pressed({
        flow: 'p2p_send_money',
        policy_major_version: userAgreementConsentMaxVersion,
        policy_minor_version: userAgreementConsentMinVersion,
      })
    )

    dispatch({ type: types.TRANSACTION.REQUEST_USER_AGREEMENT_CONSENT })

    const userFlowType = 'send'
    const url = `/myaccount/transfer/${userFlowType}/user-agreement-consent-contingency`
    post(url, payload)
      .then(response => {
        dispatch({ type: types.TRANSACTION.RESOLVE_USER_AGREEMENT_CONSENT })
        // Re-submit to proceed with transaction
        handleSubmit()
        Actions.resetScrollPosition()
      })
      .catch(err => {
        logger.error(err)
      })
  }
}

export function updateSenderReturningFromCIP(isSenderReturningFromCIP) {
  return {
    type: types.TRANSACTION.SENDER_RETURNING_FROM_CIP,
    data: isSenderReturningFromCIP,
  }
}

export function redirectSenderCipVerification(override) {
  const url = '/myaccount/transfer/flow/fi/sender-cip-verification'
  const mainWindow = getMainWindow()
  return (dispatch, getState) => {
    dispatch(Actions.setIsLoading(true))
    const state = getState()
    const data = getSenderCIPContingencyPayload(state, override)
    return postJson(url, data)
      .then(response => {
        const { redirectURL = {} } = response
        logger.info(
          `CA_CIP user can be redirected to cip flow - ${redirectURL}`
        )
        tracking(
          Events.p2p_protection_overpanel_cip_redirection_occurred({
            policy_name: override.senderCipVerification.policy,
            deeplink_url: redirectURL,
            payment_type: data.isPurchase ? 'purchase' : 'personal',
          })
        )

        mainWindow.location.assign(redirectURL)
      })
      .catch(err => {
        logger.error(
          `CA_CIP user redirection to cip flow failed. error code: ${err.code} message: ${err.message}`
        )
        dispatch(Actions.setIsLoading(false))
        dispatch(updateSenderReturningFromCIP(false))
        return dispatch(Actions.showError(err))
      })
  }
}

export function routeReturningUserFromCip() {
  return (dispatch, getState) => {
    const state = getState()
    const flowType = getFlowType()
    dispatch(Actions.setPaymentTypeSelected(false))
    dispatch(Actions.setIsLoading(false))
    dispatch(updateSenderReturningFromCIP(false))
    logger.info(`CA_CIP returning user still has contingency`)

    if (state.external && flowType !== ENV_TYPE.PAY_REQUEST) {
      routeTo(`/homepage/preview`)
    }

    return dispatch(
      UnifiedActions.togglePaymentTypeChoiceOverpanel({ show: true })
    )
  }
}

export function changeAnonymousStatus(anonymousStatus) {
  return {
    type: types.EXTERNAL.CHANGE_ANONYMOUS_STATUS,
    data: { anonymousStatus },
  }
}

function getSenderCIPContingencyPayload(state, override = {}) {
  return getSessionPayload(state, {
    ...override,
    hasSenderCipVerificationContingency:
      override.hasSenderCipVerificationContingency,
    walletSuccess: state.transaction.walletSuccess,
  })
}
